import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

interface SmartLinkProps {
  /**
   * A hash target for the current document (`#contact`, or `/#contact` while on `/`)
   * renders a native anchor; anything else renders a router link.
   */
  to: string;
  className?: string;
  ariaLabel?: string;
  children: ReactNode;
}

/**
 * The single place that decides between a native hash anchor and a client-side route link.
 *
 * Any hash target pointing at the *current* document must be a native anchor: the browser
 * re-runs "scroll to fragment" on every activation, so repeat clicks keep working. A router
 * `Link` cannot — once the URL is already `/#contact`, `location` stops changing, no effect
 * re-fires, and a second click after scrolling away does nothing. Router `Link` is also wrong
 * for a bare `#hash`, which it resolves as a *relative path*.
 *
 * Path-qualified hashes (`/#contact`) still need the router when the path differs from the
 * current one — e.g. the header and the CTAs on the 404 route, which must actually navigate.
 * `ScrollToHash` covers that case, plus cold loads and the `/contact` redirect.
 */
const SmartLink = ({ to, className, ariaLabel, children }: SmartLinkProps): JSX.Element => {
  const { pathname } = useLocation();
  const hashIndex = to.indexOf('#');

  if (hashIndex !== -1) {
    const targetPath = to.slice(0, hashIndex);
    if (targetPath === '' || targetPath === pathname) {
      return (
        <a href={to.slice(hashIndex)} aria-label={ariaLabel} className={className}>
          {children}
        </a>
      );
    }
  }

  return (
    <Link to={to} aria-label={ariaLabel} className={className}>
      {children}
    </Link>
  );
};

export default SmartLink;
